import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils/cn';
import { vibrateTap } from '@/lib/utils/haptics';

/**
 * TZ-docs/08-design-system.md §8.5.3 — tugma variantlari.
 * Ekranda 1 tadan ortiq `primary` bo'lmasin (§8.12.14) — bu qoidani sahifa darajasida saqlang.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'danger' | 'icon';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'h-14 rounded-pill bg-btn text-on-btn text-[16px] font-semibold px-6',
  secondary: 'h-14 rounded-pill bg-surface text-ink font-semibold shadow-card px-6',
  soft: 'h-12 rounded-pill bg-subtle text-ink-2 font-medium px-5',
  ghost: 'h-12 text-ink-2 font-medium px-4',
  danger: 'h-14 rounded-pill bg-danger text-white font-semibold px-6',
  icon: 'w-12 h-12 rounded-full bg-subtle grid place-items-center',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  fullWidth?: boolean;
  /** icon variant uchun majburiy — a11y §8.11 */
  'aria-label'?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading = false, fullWidth = false, disabled, className, children, onClick, ...rest },
  ref,
) {
  const isIcon = variant === 'icon';
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      onClick={(e) => {
        vibrateTap();
        onClick?.(e);
      }}
      className={cn(
        'inline-flex items-center justify-center gap-2 transition-transform duration-150 active:scale-[0.98]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2',
        'disabled:opacity-60 disabled:pointer-events-none',
        VARIANT_CLASS[variant],
        fullWidth && !isIcon && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
});

function Spinner() {
  return (
    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}
